/**
 * Sign-up route (APP-001 §8).
 *
 * Uses Clerk's hosted <SignUp /> component mounted on the /sign-up path.
 * Preserves the return path / checkout intent through the flow. No billing is
 * initiated in APP-001; plan/interval intent is only carried forward.
 */
import { SignUp, useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { parseReturnIntent } from '@/auth/returnPath';
import { track } from '@/lib/analytics';
import { AuthCard } from '@/components/navigation/AuthCard';
import { BrandMark } from '@/components/navigation/BrandMark';
import { getAppLinks } from '@/config/links';
import { AuthLegalLinks } from '@/components/navigation/AuthLegalLinks';

export function SignUpRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const intent = parseReturnIntent(location.search);
  const links = getAppLinks();

  useEffect(() => {
    if (isSignedIn) {
      track({ event: 'sign_up_completed' });
      navigate(intent.returnPath ?? '/dashboard', { replace: true });
    }
  }, [isSignedIn, intent.returnPath, navigate]);

  if (!isLoaded) return null;
  if (isSignedIn) return <Navigate to={intent.returnPath ?? '/dashboard'} replace />;

  return (
    <AuthCard>
      <div className="mb-6 flex justify-center">
        <a href={links.marketingBase} aria-label="Signup Risk home">
          <BrandMark />
        </a>
      </div>
      <SignUp
        routing="path"
        path="/sign-up"
        fallbackRedirectUrl={intent.returnPath ?? '/dashboard'}
        appearance={{
          elements: { card: 'shadow-none p-0' },
        }}
      />
      <AuthLegalLinks />
    </AuthCard>
  );
}
